import React, { useEffect, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import api from '../../services/api';

const PIP = { green: '#10b981', amber: '#f59e0b', red: '#ef4444', blue: '#3b82f6' };

function NowCard({ label, value, hint, pip, onClick, isDark }) {
  const muted = isDark ? 'rgba(248,250,252,0.45)' : '#94a3b8';
  const textMain = isDark ? '#f8fafc' : '#0f172a';
  const textSub = isDark ? 'rgba(248,250,252,0.68)' : '#475569';
  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)';

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
      sx={{
        background: cardBg,
        backdropFilter: 'blur(20px)',
        border: cardBorder,
        borderRadius: '16px',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        cursor: 'pointer',
        minHeight: 132,
        boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.25)' : '0 2px 12px rgba(0,0,0,0.05)',
        '&:hover': { borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', color: muted, flex: 1 }}>
          {label}
        </Typography>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: pip, boxShadow: `0 0 6px ${pip}` }} />
      </Box>
      <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', color: textMain, lineHeight: 1.15 }}>
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: textSub }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

const DashboardLedgers = () => {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const { hasFeature } = useSubscription();
  const { themeMode } = useThemeContext();
  const navigate = useNavigate();
  const isDark = themeMode !== 'light';

  const showPayables = hasFeature('payables');
  const showPantry = hasFeature('pantry');
  const [payables, setPayables] = useState(null);
  const [pantry, setPantry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!showPayables && !showPantry) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const jobs = [];
    if (showPayables) {
      jobs.push(
        api.get('/payables/month').then((res) => {
          if (!cancelled) setPayables(res.data?.data || res.data);
        }).catch(() => { if (!cancelled) setPayables(null); })
      );
    }
    if (showPantry) {
      jobs.push(
        api.get('/pantry/list').then((res) => {
          if (!cancelled) setPantry(res.data?.data || res.data);
        }).catch(() => { if (!cancelled) setPantry(null); })
      );
    }
    Promise.all(jobs).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [showPayables, showPantry]);

  if (!showPayables && !showPantry) return null;

  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)';

  if (loading) {
    return (
      <Box sx={{ display: 'contents' }}>
        {showPayables && <Box sx={{ background: cardBg, border: cardBorder, borderRadius: '16px', p: '20px' }}><Skeleton width="40%" /><Skeleton width="70%" sx={{ mt: 1 }} /></Box>}
        {showPantry && <Box sx={{ background: cardBg, border: cardBorder, borderRadius: '16px', p: '20px' }}><Skeleton width="40%" /><Skeleton width="70%" sx={{ mt: 1 }} /></Box>}
      </Box>
    );
  }

  const tiles = [];
  if (showPayables && payables) {
    const remaining = payables.totals?.remaining || 0;
    const unpaid = payables.totals?.unpaidCount || 0;
    const status = payables.coverage?.status || 'green';
    const coverageLabel = status === 'red'
      ? t('payables.coverageRed', 'Short')
      : status === 'amber'
        ? t('payables.coverageAmber', 'Tight')
        : t('payables.coverageGreen', 'Covered');
    tiles.push(
      <NowCard
        key="payables"
        isDark={isDark}
        label={t('payables.title', 'Payables')}
        value={unpaid === 0 ? coverageLabel : formatAmount(remaining)}
        hint={unpaid === 0
          ? t('payables.thisMonth', 'This month')
          : `${unpaid} ${t('payables.openItems', 'open')} · ${coverageLabel}`}
        pip={PIP[status] || PIP.green}
        onClick={() => navigate('/dashboard/payables')}
      />
    );
  }
  if (showPantry && pantry) {
    const count = pantry.count || 0;
    const estimate = pantry.estimate || 0;
    tiles.push(
      <NowCard
        key="pantry"
        isDark={isDark}
        label={t('pantry.title', 'Pantry')}
        value={count === 0 ? t('pantry.stripEmpty', 'Nothing to buy') : `${count} ${t('pantry.toBuy', 'to buy')}`}
        hint={count === 0 ? t('pantry.have', 'Have') : (estimate > 0 ? `~${formatAmount(estimate)}` : t('pantry.openList', 'List'))}
        pip={count === 0 ? PIP.green : PIP.blue}
        onClick={() => navigate('/dashboard/pantry?tab=need')}
      />
    );
  }

  if (!tiles.length) return null;
  return <Box sx={{ display: 'contents' }}>{tiles}</Box>;
};

export default DashboardLedgers;
